import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BottomTabNavigation from '../../components/openplay/BottomTabNavigation';
import Icon from '../../components/openplay/OpenPlayIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabaseClient';
import { resizeImageToWidth } from '../../lib/imageUtils';

const generateToken = () => {
  const arr = new Uint8Array(12);
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  const fallback = `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random()
    .toString(16)
    .slice(2)}`;
  return fallback.replace(/[^a-f0-9]/gi, '').slice(0, 24).padEnd(24, '0');
};

const getFileExt = (file) => {
  const type = String(file?.type || '').toLowerCase();
  if (type === 'image/webp') return 'webp';
  if (type === 'image/png') return 'png';
  if (type === 'image/jpeg' || type === 'image/jpg') return 'jpg';
  if (type === 'image/gif') return 'gif';
  return 'jpg';
};

const storagePathFromUrl = (url) => {
  const trimmed = String(url || '').trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    const token = '/storage/v1/object/public/session-images/';
    const idx = parsed.pathname.indexOf(token);
    if (idx === -1) return null;
    return decodeURIComponent(parsed.pathname.slice(idx + token.length));
  } catch {
    return null;
  }
};

const emptyForm = {
  name: '',
  indoor: false,
  address: '',
  venueLat: '',
  venueLng: '',
  courtCount: '',
  floorMaterial: '',
  websiteUrl: '',
  businessHours: '',
  priceInfo: [''],
  amenities: {
    parking: false,
    shower: false,
    ballMachine: false,
  },
  equipmentRental: false,
  supplies: '',
};

const CodeFormPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const codeId = String(params?.codeId || '').trim();
  const isNew = !codeId || codeId === 'new';

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [imageDrafts, setImageDrafts] = useState([]);
  const [existingGalleryUrls, setExistingGalleryUrls] = useState([]);
  const [coverSelection, setCoverSelection] = useState(null);
  const [existingCoverThumbUrl, setExistingCoverThumbUrl] = useState('');
  const [removedGalleryUrls, setRemovedGalleryUrls] = useState([]);

  const [venueSearchOpen, setVenueSearchOpen] = useState(false);
  const [venueSearchQuery, setVenueSearchQuery] = useState('');
  const [venueSearchResults, setVenueSearchResults] = useState([]);
  const [venueSearchLoading, setVenueSearchLoading] = useState(false);
  const [venueSearchError, setVenueSearchError] = useState('');

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      if (isNew) {
        setForm(emptyForm);
        setImageDrafts([]);
        setExistingGalleryUrls([]);
        setCoverSelection(null);
        setExistingCoverThumbUrl('');
        setRemovedGalleryUrls([]);
        return;
      }

      setLoading(true);
      setLoadError('');
      try {
        const { data, error } = await supabase.from('pickleball_codes').select('*').eq('id', codeId).maybeSingle();
        if (!mounted) return;
        if (error || !data) {
          setLoadError(error?.message || '코드 정보를 불러오지 못했습니다.');
          return;
        }

        setForm({
          name: String(data?.name || ''),
          indoor: Boolean(data?.indoor),
          address: String(data?.address || ''),
          venueLat: data?.venue_lat != null ? String(data.venue_lat) : '',
          venueLng: data?.venue_lng != null ? String(data.venue_lng) : '',
          courtCount: data?.court_count != null ? String(data.court_count) : '',
          floorMaterial: String(data?.floor_material || ''),
          websiteUrl: String(data?.website_url || ''),
          businessHours: String(data?.business_hours || ''),
          priceInfo: Array.isArray(data?.price_info) && data.price_info.length > 0 ? data.price_info : [''],
          amenities: {
            parking: Boolean(data?.amenities?.parking),
            shower: Boolean(data?.amenities?.shower),
            ballMachine: Boolean(data?.amenities?.ballMachine),
          },
          equipmentRental: Boolean(data?.equipment_rental),
          supplies: String(data?.supplies || ''),
        });

        const gallery = Array.isArray(data?.gallery_urls) ? data.gallery_urls : [];
        const cleaned = gallery.map((u) => String(u || '').trim()).filter(Boolean).slice(0, 10);
        setExistingGalleryUrls(cleaned);
        setRemovedGalleryUrls([]);
        const cover = String(data?.cover_image_url || '').trim();
        setExistingCoverThumbUrl(String(data?.cover_image_thumb_url || '').trim());
        if (cover) {
          setCoverSelection({ kind: 'existing', value: cover });
        } else if (cleaned[0]) {
          setCoverSelection({ kind: 'existing', value: cleaned[0] });
        } else {
          setCoverSelection(null);
        }
        setImageDrafts([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [codeId, isNew]);

  const handleImageSelect = (files) => {
    const selected = Array.from(files || []);
    if (selected.length === 0) return;
    const currentCount = existingGalleryUrls.length + imageDrafts.length;
    if (currentCount + selected.length > 10) {
      alert('이미지는 최대 10장까지 등록할 수 있습니다.');
      return;
    }
    const nextDrafts = selected.map((file, idx) => ({
      id: `${Date.now()}-${idx}-${file?.name || 'image'}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setImageDrafts((p) => [...(Array.isArray(p) ? p : []), ...nextDrafts]);
    if (!coverSelection && nextDrafts[0]?.id) {
      setCoverSelection({ kind: 'draft', value: nextDrafts[0].id });
    }
  };

  const removeDraft = (id) => {
    setImageDrafts((p) => {
      const next = (Array.isArray(p) ? p : []).filter((d) => d.id !== id);
      if (coverSelection?.kind === 'draft' && coverSelection?.value === id) {
        const fallbackExisting = existingGalleryUrls[0];
        const fallbackDraft = next[0]?.id;
        setCoverSelection(
          fallbackExisting
            ? { kind: 'existing', value: fallbackExisting }
            : fallbackDraft
              ? { kind: 'draft', value: fallbackDraft }
              : null
        );
      }
      return next;
    });
  };

  const removeExisting = (url) => {
    const u = String(url || '').trim();
    setExistingGalleryUrls((p) => {
      const next = (Array.isArray(p) ? p : []).filter((x) => String(x || '').trim() !== u);
      if (coverSelection?.kind === 'existing' && String(coverSelection?.value || '').trim() === u) {
        const fallbackExisting = next[0];
        const fallbackDraft = imageDrafts[0]?.id;
        setCoverSelection(
          fallbackExisting
            ? { kind: 'existing', value: fallbackExisting }
            : fallbackDraft
              ? { kind: 'draft', value: fallbackDraft }
              : null
        );
      }
      return next;
    });
    if (u) {
      setRemovedGalleryUrls((p) => [...(Array.isArray(p) ? p : []), u]);
    }
  };

  const handleVenueSearch = async (query) => {
    const q = String(query || '').trim();
    if (!q) return [];

    const normalize = (rows) => {
      const list = Array.isArray(rows) ? rows : [];
      return list.map((r) => {
        const roadAddress = String(r?.roadAddress || '').trim();
        const jibunAddress = String(r?.jibunAddress || '').trim();
        const englishAddress = String(r?.englishAddress || '').trim();
        const x = r?.x != null ? Number(r.x) : null;
        const y = r?.y != null ? Number(r.y) : null;
        return {
          roadAddress: roadAddress || null,
          jibunAddress: jibunAddress || null,
          englishAddress: englishAddress || null,
          x: Number.isFinite(x) ? x : null,
          y: Number.isFinite(y) ? y : null,
          placeName: r?.placeName ? String(r.placeName) : null,
        };
      });
    };

    try {
      const { data: placeData } = await supabase.functions.invoke('naver-place-search', {
        body: { query: q, display: 10 },
      });

      if (placeData?.ok) {
        const normalized = normalize(placeData?.results);
        if (normalized.length > 0) return normalized;
      }
    } catch {
      // ignore
    }

    const { data } = await supabase.functions.invoke('naver-geocode', {
      body: { query: q, count: 10, language: 'kor' },
    });

    if (!data?.ok) {
      throw new Error(data?.error || '네이버 지도 검색에 실패했습니다.');
    }

    return normalize(data?.results);
  };

  const runVenueSearch = async () => {
    const q = String(venueSearchQuery || '').trim();
    if (!q) {
      setVenueSearchError('검색어를 입력하세요');
      return;
    }
    setVenueSearchLoading(true);
    setVenueSearchError('');
    try {
      const results = await handleVenueSearch(q);
      setVenueSearchResults(Array.isArray(results) ? results : []);
      if (!Array.isArray(results) || results.length === 0) {
        setVenueSearchError('검색 결과가 없습니다');
      }
    } catch (e) {
      setVenueSearchResults([]);
      setVenueSearchError(e?.message || '검색에 실패했습니다');
    } finally {
      setVenueSearchLoading(false);
    }
  };

  const selectVenue = (row) => {
    const placeName = String(row?.placeName || '').trim();
    const address = String(row?.roadAddress || row?.jibunAddress || '').trim();
    const lat = row?.y != null ? String(row.y) : '';
    const lng = row?.x != null ? String(row.x) : '';

    setForm((p) => ({
      ...(p || {}),
      name: placeName || p?.name,
      address,
      venueLat: lat,
      venueLng: lng,
    }));

    setVenueSearchOpen(false);
    setVenueSearchResults([]);
    setVenueSearchError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const name = String(form?.name || '').trim();
    const address = String(form?.address || '').trim();
    const lat = Number.parseFloat(String(form?.venueLat || '').trim());
    const lng = Number.parseFloat(String(form?.venueLng || '').trim());

    if (!name || !address) {
      alert('시설명과 주소를 입력해주세요.');
      return;
    }

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      alert('네이버 장소 검색을 통해 주소를 선택해주세요.');
      return;
    }

    const totalImages = existingGalleryUrls.length + imageDrafts.length;
    if (totalImages < 1) {
      alert('대표 이미지를 최소 1장 등록해주세요.');
      return;
    }

    if (totalImages > 10) {
      alert('이미지는 최대 10장까지 등록할 수 있습니다.');
      return;
    }

    setSubmitting(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (!userId) {
        alert('로그인이 필요합니다.');
        return;
      }

      const uploadDrafts = async (targetId) => {
        const uploaded = [];
        for (let i = 0; i < imageDrafts.length; i += 1) {
          const draft = imageDrafts[i];
          if (!draft?.file) continue;
          const ext = getFileExt(draft.file);
          const path = `${userId}/codes/${targetId}/${generateToken()}.${ext}`;
          const { error: uploadError } = await supabase.storage.from('session-images').upload(path, draft.file, {
            cacheControl: '3600',
            upsert: false,
            contentType: draft.file?.type || 'image/jpeg',
          });
          if (uploadError) {
            throw new Error(uploadError?.message || '이미지 업로드에 실패했습니다.');
          }
          const { data: publicData } = supabase.storage.from('session-images').getPublicUrl(path);
          const publicUrl = String(publicData?.publicUrl || '').trim();
          if (publicUrl) uploaded.push({ id: draft.id, url: publicUrl, file: draft.file });
        }
        return uploaded;
      };

      const uploadCoverThumb = async ({ targetId, coverFile }) => {
        if (!coverFile) return null;
        const thumbFile = await resizeImageToWidth(coverFile, {
          width: 100,
          mimeType: 'image/jpeg',
          quality: 0.82,
          fileName: 'thumb.jpg',
        });
        const path = `${userId}/codes/${targetId}/thumb_${generateToken()}.jpg`;
        const { error: uploadError } = await supabase.storage.from('session-images').upload(path, thumbFile, {
          cacheControl: '3600',
          upsert: false,
          contentType: 'image/jpeg',
        });
        if (uploadError) {
          throw new Error(uploadError?.message || '대표 이미지 썸네일 업로드에 실패했습니다.');
        }
        const { data: publicData } = supabase.storage.from('session-images').getPublicUrl(path);
        return String(publicData?.publicUrl || '').trim() || null;
      };

      const resolveCoverUrl = ({ finalUrls, uploadedById }) => {
        if (coverSelection?.kind === 'existing') {
          const url = String(coverSelection?.value || '').trim();
          if (url && finalUrls.includes(url)) return url;
        }
        if (coverSelection?.kind === 'draft') {
          const id = String(coverSelection?.value || '').trim();
          const url = uploadedById.get(id);
          if (url && finalUrls.includes(url)) return url;
        }
        return finalUrls[0] || null;
      };

      const resolveCoverFile = ({ coverUrl, uploaded, finalUrls }) => {
        if (!coverUrl) return null;
        if (coverSelection?.kind === 'draft') {
          const id = String(coverSelection?.value || '').trim();
          const hit = uploaded.find((u) => String(u?.id || '').trim() === id);
          return hit?.file || null;
        }
        const match = uploaded.find((u) => String(u?.url || '').trim() === String(coverUrl || '').trim());
        if (match?.file) return match.file;
        const first = uploaded[0];
        if (first?.file && finalUrls?.[0] && String(finalUrls[0]).trim() === String(first.url || '').trim()) {
          return first.file;
        }
        return null;
      };

      const payload = {
        name,
        indoor: Boolean(form?.indoor),
        address,
        venue_lat: lat,
        venue_lng: lng,
        court_count: form?.courtCount ? Number.parseInt(form.courtCount, 10) : null,
        floor_material: String(form?.floorMaterial || '').trim() || null,
        website_url: String(form?.websiteUrl || '').trim() || null,
        business_hours: String(form?.businessHours || '').trim() || null,
        price_info: (Array.isArray(form?.priceInfo) ? form.priceInfo : [])
          .map((item) => String(item || '').trim())
          .filter(Boolean),
        amenities: {
          parking: Boolean(form?.amenities?.parking),
          shower: Boolean(form?.amenities?.shower),
          ballMachine: Boolean(form?.amenities?.ballMachine),
        },
        equipment_rental: Boolean(form?.equipmentRental),
        supplies: String(form?.supplies || '').trim() || null,
        updated_at: new Date().toISOString(),
      };

      if (!isNew) {
        const uploaded = await uploadDrafts(codeId);
        const uploadedUrls = uploaded.map((u) => u.url);
        const finalUrls = [...existingGalleryUrls, ...uploadedUrls].filter(Boolean).slice(0, 10);
        const uploadedById = new Map(uploaded.map((u) => [u.id, u.url]));
        const coverUrl = resolveCoverUrl({ finalUrls, uploadedById });

        const coverFile = resolveCoverFile({ coverUrl, uploaded, finalUrls });
        let coverThumbUrl = existingCoverThumbUrl || null;
        if (coverFile) {
          coverThumbUrl = await uploadCoverThumb({ targetId: codeId, coverFile });
        }

        const { error } = await supabase
          .from('pickleball_codes')
          .update({
            ...payload,
            cover_image_url: coverUrl,
            cover_image_thumb_url: coverThumbUrl,
            gallery_urls: finalUrls,
          })
          .eq('id', codeId);

        if (error) throw new Error(error?.message || '수정에 실패했습니다.');

        const removeTargets = removedGalleryUrls.filter((u) => !finalUrls.includes(u));
        const removePaths = removeTargets.map(storagePathFromUrl).filter(Boolean);
        if (removePaths.length > 0) {
          await supabase.storage.from('session-images').remove(removePaths);
        }

        navigate('/openplay/code-management');
        return;
      }

      const { data: created, error } = await supabase
        .from('pickleball_codes')
        .insert({
          ...payload,
          created_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (error || !created?.id) {
        throw new Error(error?.message || '등록에 실패했습니다.');
      }

      const uploaded = await uploadDrafts(created.id);
      const uploadedUrls = uploaded.map((u) => u.url);
      const finalUrls = [...existingGalleryUrls, ...uploadedUrls].filter(Boolean).slice(0, 10);
      const uploadedById = new Map(uploaded.map((u) => [u.id, u.url]));
      const coverUrl = resolveCoverUrl({ finalUrls, uploadedById });

      const coverFile = resolveCoverFile({ coverUrl, uploaded, finalUrls });
      const coverThumbUrl = coverFile ? await uploadCoverThumb({ targetId: created.id, coverFile }) : null;

      const { error: patchError } = await supabase
        .from('pickleball_codes')
        .update({
          cover_image_url: coverUrl,
          cover_image_thumb_url: coverThumbUrl,
          gallery_urls: finalUrls,
        })
        .eq('id', created.id);

      if (patchError) {
        throw new Error(patchError?.message || '이미지 저장에 실패했습니다.');
      }

      navigate('/openplay/code-management');
    } catch (e) {
      alert(e?.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const title = useMemo(() => (isNew ? '코드 등록' : '코드 수정'), [isNew]);

  return (
    <div className="min-h-screen bg-background">
      <div className="md:pl-[240px]">
        <main className="pt-6 md:pt-10 pb-[calc(84px+env(safe-area-inset-bottom,16px))] md:pb-0">
          <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex items-start justify-between gap-4 mb-6 md:mb-8">
              <div className="min-w-0">
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">{title}</h1>
                <p className="text-base md:text-lg caption text-muted-foreground">필수 정보를 입력해 주세요.</p>
              </div>
              <Button type="button" variant="outline" onClick={() => navigate('/openplay/code-management')}>
                목록
              </Button>
            </div>

            {loadError && (
              <div className="mb-6 bg-error/10 border border-error/20 rounded-xl p-4">
                <p className="text-sm text-error">{loadError}</p>
              </div>
            )}

            {loading ? (
              <div className="bg-card border border-border rounded-xl p-6 text-sm text-muted-foreground">로딩 중...</div>
            ) : (
              <div className="bg-card border border-border rounded-xl p-5">
                <form className="space-y-5" onSubmit={handleSubmit}>
                  <Input
                    label="시설명"
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="장소 이름"
                    required
                  />

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">주소</label>
                    <div className="flex gap-2">
                      <Input
                        value={form.address}
                        onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                        placeholder="주소 검색"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setVenueSearchQuery(String(form?.address || '').trim());
                          setVenueSearchResults([]);
                          setVenueSearchError('');
                          setVenueSearchOpen(true);
                        }}
                        className="h-11 px-3 rounded-lg border border-border bg-background hover:bg-muted transition-smooth text-sm font-semibold"
                      >
                        검색
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        label="위도"
                        value={form.venueLat}
                        onChange={(e) => setForm((p) => ({ ...p, venueLat: e.target.value }))}
                      />
                      <Input
                        label="경도"
                        value={form.venueLng}
                        onChange={(e) => setForm((p) => ({ ...p, venueLng: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">대표 이미지</label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleImageSelect(e?.target?.files)}
                      className="w-full text-sm text-muted-foreground"
                    />
                    <p className="text-[11px] text-muted-foreground">1~10장 업로드 · 대표 이미지 선택</p>
                    {(existingGalleryUrls.length > 0 || imageDrafts.length > 0) && (
                      <div className="grid grid-cols-3 gap-2">
                        {existingGalleryUrls.map((url) => (
                          <div key={url} className="relative">
                            <button
                              type="button"
                              onClick={() => setCoverSelection({ kind: 'existing', value: url })}
                              className={`relative w-full aspect-square rounded-lg overflow-hidden border ${
                                coverSelection?.kind === 'existing' && String(coverSelection?.value || '').trim() === url
                                  ? 'border-primary ring-2 ring-primary/40'
                                  : 'border-border'
                              }`}
                            >
                              <img src={url} alt="preview" className="w-full h-full object-cover" />
                              {coverSelection?.kind === 'existing' && String(coverSelection?.value || '').trim() === url && (
                                <span className="absolute left-1 top-1 text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded">대표</span>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => removeExisting(url)}
                              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-background border border-border shadow-elevation-1 flex items-center justify-center text-muted-foreground hover:text-foreground"
                            >
                              <Icon name="XMarkIcon" size={12} />
                            </button>
                          </div>
                        ))}
                        {imageDrafts.map((draft) => (
                          <div key={draft.id} className="relative">
                            <button
                              type="button"
                              onClick={() => setCoverSelection({ kind: 'draft', value: draft.id })}
                              className={`relative w-full aspect-square rounded-lg overflow-hidden border ${
                                coverSelection?.kind === 'draft' && coverSelection?.value === draft.id
                                  ? 'border-primary ring-2 ring-primary/40'
                                  : 'border-border'
                              }`}
                            >
                              <img src={draft.previewUrl} alt="preview" className="w-full h-full object-cover" />
                              {coverSelection?.kind === 'draft' && coverSelection?.value === draft.id && (
                                <span className="absolute left-1 top-1 text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground rounded">대표</span>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => removeDraft(draft.id)}
                              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-background border border-border shadow-elevation-1 flex items-center justify-center text-muted-foreground hover:text-foreground"
                            >
                              <Icon name="XMarkIcon" size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="radio"
                        name="indoor"
                        checked={form.indoor}
                        onChange={() => setForm((p) => ({ ...p, indoor: true }))}
                      />
                      실내
                    </label>
                    <label className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="radio"
                        name="indoor"
                        checked={!form.indoor}
                        onChange={() => setForm((p) => ({ ...p, indoor: false }))}
                      />
                      실외
                    </label>
                  </div>

                  <Input
                    label="영업시간"
                    value={form.businessHours}
                    onChange={(e) => setForm((p) => ({ ...p, businessHours: e.target.value }))}
                    placeholder="예: 평일 09:00-22:00 / 주말 10:00-20:00"
                  />

                  <Input
                    label="웹사이트 URL"
                    value={form.websiteUrl}
                    onChange={(e) => setForm((p) => ({ ...p, websiteUrl: e.target.value }))}
                    placeholder="https://"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="코트 수"
                      value={form.courtCount}
                      onChange={(e) => setForm((p) => ({ ...p, courtCount: e.target.value }))}
                      placeholder="예: 6"
                    />
                    <Input
                      label="바닥 재질"
                      value={form.floorMaterial}
                      onChange={(e) => setForm((p) => ({ ...p, floorMaterial: e.target.value }))}
                      placeholder="예: 아크릴"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-foreground">가격 정보</p>
                      <button
                        type="button"
                        onClick={() =>
                          setForm((p) => ({
                            ...p,
                            priceInfo: [...(Array.isArray(p.priceInfo) ? p.priceInfo : []), ''],
                          }))
                        }
                        className="text-xs font-semibold text-primary"
                      >
                        + 추가
                      </button>
                    </div>
                    <div className="space-y-2">
                      {(Array.isArray(form.priceInfo) ? form.priceInfo : []).map((item, idx) => (
                        <div key={`price-${idx}`} className="flex items-center gap-2">
                          <Input
                            value={item}
                            onChange={(e) => {
                              const value = e.target.value;
                              setForm((p) => {
                                const list = Array.isArray(p.priceInfo) ? [...p.priceInfo] : [];
                                list[idx] = value;
                                return { ...p, priceInfo: list };
                              });
                            }}
                            placeholder="예: 1회 10,000원 / 월 80,000원"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setForm((p) => {
                                const list = Array.isArray(p.priceInfo) ? [...p.priceInfo] : [];
                                list.splice(idx, 1);
                                return { ...p, priceInfo: list.length > 0 ? list : [''] };
                              })
                            }
                            className="w-10 h-10 rounded-lg border border-border text-muted-foreground hover:text-foreground"
                          >
                            <Icon name="XMarkIcon" size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-foreground">편의 시설</p>
                    <div className="grid grid-cols-1 gap-2 text-sm text-foreground">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={form.amenities.parking}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              amenities: { ...p.amenities, parking: e.target.checked },
                            }))
                          }
                        />
                        주차 가능
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={form.amenities.shower}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              amenities: { ...p.amenities, shower: e.target.checked },
                            }))
                          }
                        />
                        샤워실
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={form.amenities.ballMachine}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              amenities: { ...p.amenities, ballMachine: e.target.checked },
                            }))
                          }
                        />
                        정구기
                      </label>
                    </div>
                  </div>

                  <label className="flex items-center gap-2 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={form.equipmentRental}
                      onChange={(e) => setForm((p) => ({ ...p, equipmentRental: e.target.checked }))}
                    />
                    장비 대여 가능
                  </label>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-foreground">준비물</label>
                    <textarea
                      value={form.supplies}
                      onChange={(e) => setForm((p) => ({ ...p, supplies: e.target.value }))}
                      className="w-full min-h-[90px] px-3 py-2 rounded-lg border border-border bg-background text-sm resize-none"
                      placeholder="예: 라켓, 실내화"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button type="submit" fullWidth disabled={submitting}>
                      {submitting ? '저장 중...' : isNew ? '등록하기' : '수정하기'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => navigate('/openplay/code-management')} fullWidth>
                      취소
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </main>
      </div>

      {venueSearchOpen && (
        <div
          className="fixed inset-0 z-[10001] flex items-start justify-center pt-[10vh] px-4"
          onClick={(e) => {
            if (e?.target === e?.currentTarget) setVenueSearchOpen(false);
          }}
        >
          <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-10" aria-hidden="true" />
          <div className="relative z-20 w-full max-w-2xl bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
            <div className="bg-card border-b border-border p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-heading font-semibold text-foreground">장소 검색</h2>
                <p className="text-xs caption text-muted-foreground mt-0.5">주소를 검색하고 선택하세요</p>
              </div>
              <button
                type="button"
                className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-athletic"
                onClick={() => setVenueSearchOpen(false)}
                aria-label="닫기"
              >
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="flex gap-2">
                <input
                  className="flex-1 px-4 py-3 bg-muted/30 border border-border rounded-md text-foreground"
                  value={venueSearchQuery}
                  onChange={(e) => setVenueSearchQuery(e?.target?.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      runVenueSearch();
                    }
                  }}
                  placeholder="예: 서울 서대문구 이화여대길 52"
                />
                <button
                  type="button"
                  onClick={runVenueSearch}
                  disabled={venueSearchLoading}
                  className="h-11 px-4 rounded-lg bg-primary text-primary-foreground font-semibold disabled:opacity-60"
                >
                  검색
                </button>
              </div>

              {venueSearchError && (
                <div className="bg-error/10 border border-error/20 rounded-md p-3 flex items-start gap-2">
                  <Icon name="ExclamationTriangleIcon" size={18} className="mt-0.5 text-error" />
                  <p className="text-sm text-error">{venueSearchError}</p>
                </div>
              )}

              <div className="max-h-[60vh] overflow-y-auto divide-y divide-border border border-border rounded-lg">
                {(venueSearchResults || []).map((row, idx) => {
                  const placeName = String(row?.placeName || '').trim();
                  const address = String(row?.roadAddress || row?.jibunAddress || '').trim();
                  const sub = String(row?.jibunAddress || '').trim();
                  return (
                    <button
                      key={`${address}-${idx}`}
                      type="button"
                      className="w-full text-left p-4 hover:bg-muted transition-athletic"
                      onClick={() => selectVenue(row)}
                    >
                      {placeName && <p className="text-sm font-semibold text-foreground">{placeName}</p>}
                      <p className="text-sm font-medium text-foreground">{address || '주소'}</p>
                      {sub && sub !== address && <p className="text-xs caption text-muted-foreground mt-1">{sub}</p>}
                      <p className="text-xs caption text-muted-foreground mt-1">위치: {row?.y}, {row?.x}</p>
                    </button>
                  );
                })}

                {Array.isArray(venueSearchResults) &&
                  venueSearchResults.length === 0 &&
                  !venueSearchLoading &&
                  !venueSearchError && (
                    <div className="p-4">
                      <p className="text-sm text-muted-foreground">주소 형태로 검색해보세요.</p>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      <BottomTabNavigation userRole="player" />
    </div>
  );
};

export default CodeFormPage;
